import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { SUBTYPE } from './constants';
import { nodes, Actions } from './modules/DOM';
import ImageModal from './components/Modal/ImageModal';
import Modal from './components/Modal/Modal';
import Store, { getTheme } from './modules/DataStore';
import MainMenu from './components/Menu/MainMenu';
import PostList from './components/Post/PostList';
import PostDetail from './components/PostDetail/PostDetail';
import PostsBar from './components/BottomBar/PostsBar';
import DetailsBar from './components/BottomBar/DetailsBar';
import Icon from './components/Icons/Icon';
import Portal from './components/Portal/Portal';
import SubredditAdder from './components/SubredditAdder/SubredditAdder';
import ChannelCreator from './components/ChannelCreator/ChannelCreator';
import DefaultData from './modules/DefaultData';
import { clientID } from './config';
import Snoowrap from 'snoowrap';
import './app.css';
import { setAccentColors, setTheme } from './modules/ThemeSwitcher';
import { useLazyStyles } from './hooks/useLazyStyles';
import { useMatchMedia } from './hooks/useMatchMedia';
import { Message } from './components/Message/Message';
import {
  closeModal,
  openImageGallery,
  openSubreddit,
  showMessage,
} from './actions';
import PreferencesDialog from './components/PreferencesMenu/PreferencesDialog';
import { getPreferences } from './modules/Preferences';
import { ImageGallery } from './components/ImageGallery/ImageGallery';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './store/store';
import {
  selectIsImageModalOpen,
  selectModalImageUrl,
} from './store/modal/imageModalSlice';
import {
  closePreferencesDialog,
  openPreferencesDialog,
  selectMainView,
  selectPreferencesDialogOpen,
} from './store/ui/uiSlice';
import { selectOverlay, setMessage } from './store/overlay/overlaySlice';
import {
  selectExpandedReplies,
  selectBigThumbnails,
  selectPreferences,
  setExpandedReplies,
  setBigThumbnails,
  setPreferences,
} from './store/preferences/preferencesSlice';
import { selectPostsViewId, selectSelectedPost } from './store/post/postSlice';
import {
  selectSelection,
  setSelection,
} from './store/subscription/subscriptionSlice';
import {
  selectSubreddits,
  setSubreddits,
} from './store/subreddit/subredditSlice';
import { selectChannels, setChannels } from './store/channel/channelSlice';
import { selectUsername, setUsername } from './store/user/userSlice';
// import { smoothScrollY, ScrollDirection } from '../../JK/src/JK';

const loadCenteredLayoutStyles = () => import('./styles/CenteredLayout.css');

function App({ reddit }) {
  const dispatch = useDispatch();
  const isCenteredLayoutViewport = useMatchMedia('(min-width: 1080px)');
  const {
    showingSubredditAdder,
    showingChannelCreator,
    message,
    galleryImages,
  } = useSelector(selectOverlay);
  const selection = useSelector(selectSelection);
  const selectedPost = useSelector(selectSelectedPost);
  const username = useSelector(selectUsername);
  const expandedReplies = useSelector(selectExpandedReplies);
  const bigThumbnails = useSelector(selectBigThumbnails);
  const currentPreferences = useSelector(selectPreferences);
  const shouldLoadCenteredLayoutStyles =
    currentPreferences.centeredLayout && isCenteredLayoutViewport;
  useLazyStyles(
    shouldLoadCenteredLayoutStyles,
    loadCenteredLayoutStyles,
    'Centered Layout'
  );
  const postsViewId = useSelector(selectPostsViewId);
  const subreddits = useSelector(selectSubreddits);
  const channels = useSelector(selectChannels);

  useEffect(() => {
    // Set initial Preferences
    setTheme(getTheme());
    setAccentColors(
      currentPreferences.colorL,
      currentPreferences.colorD,
      currentPreferences.blackDarkBg
    );
    Actions.setBarsPosition(currentPreferences.barsPosition);

    // Make Initial Requests - Get current user
    reddit
      .getMe()
      .then(me => {
        dispatch(setUsername(me.name));
        Store.set(Store.keys.USERNAME, me.name);
      })
      .catch(error => {
        showMessage(`Error requesting initial data: ${error}`);
        console.error('Error requesting initial data', error);
      });
  }, []);

  useEffect(() => {
    Store.set(Store.keys.SELECTION, selection);
  }, [selection]);

  useEffect(() => {
    Store.set(Store.keys.EXPANDED_REPLIES, expandedReplies);
  }, [expandedReplies]);

  useEffect(() => {
    Store.set(Store.keys.BIG_THUMBNAILS, bigThumbnails);
  }, [bigThumbnails]);

  useEffect(() => {
    Store.set(Store.keys.SUBREDDITS, subreddits);
  }, [subreddits]);

  useEffect(() => {
    Store.set(Store.keys.CHANNELS, channels);
  }, [channels]);

  useEffect(() => {
    setTheme(currentPreferences.theme);
  }, [currentPreferences.theme]);

  useEffect(() => {
    Actions.setBarsPosition(currentPreferences.barsPosition);
  }, [currentPreferences.barsPosition]);

  useEffect(() => {
    Actions.setCenteredLayout(currentPreferences.centeredLayout);
  }, [currentPreferences.centeredLayout]);

  useEffect(() => {
    setAccentColors(
      currentPreferences.colorL,
      currentPreferences.colorD,
      currentPreferences.blackDarkBg
    );
  }, [
    currentPreferences.colorL,
    currentPreferences.colorD,
    currentPreferences.blackDarkBg,
  ]);

  const showingImageModal = useSelector(selectIsImageModalOpen);
  const modalImageUrl = useSelector(selectModalImageUrl);
  const showingOnMainView = useSelector(selectMainView);
  const preferencesDialogOpen = useSelector(selectPreferencesDialogOpen);

  const showingPosts = showingOnMainView === 'posts';

  const isSubredditSubscribed =
    selection.type === SUBTYPE.SUBREDDIT &&
    subreddits
      .map(subs => subs.toLowerCase())
      .includes(selection.name.toLowerCase());

  return (
    <>
      <Portal mountNode={nodes.menuRoot}>
        <MainMenu selection={selection} username={username} reddit={reddit} />
      </Portal>

      <Portal mountNode={nodes.menuBarRoot}></Portal>

      <Portal mountNode={nodes.extraColumnPreferencesRoot}>
        <button
          type="button"
          className="reset-button ExtraColumn__preferences"
          onClick={() => dispatch(openPreferencesDialog())}
          aria-haspopup="dialog"
          aria-expanded={preferencesDialogOpen}
        >
          <Icon name="settings" size="1rem" />
          <span>Preferences</span>
        </button>
      </Portal>

      <Portal mountNode={nodes.postsRoot}>
        <PostList
          key={postsViewId}
          showing={showingPosts}
          currentPost={selectedPost}
          subscription={selection}
          reddit={reddit}
          bigThumbnails={bigThumbnails}
          blurNSFW={currentPreferences.blurNSFW}
        />
      </Portal>

      {preferencesDialogOpen && (
        <Portal mountNode={nodes.modalRoot}>
          <PreferencesDialog
            onClose={() => dispatch(closePreferencesDialog())}
            expandedReplies={expandedReplies}
            bigThumbnails={bigThumbnails}
            preferences={currentPreferences}
          />
        </Portal>
      )}

      <Portal mountNode={nodes.postsBarRoot}>
        <PostsBar
          name={selection.name}
          type={selection.type}
          isSubscribed={isSubredditSubscribed}
          reddit={reddit}
        />
      </Portal>

      <Portal mountNode={nodes.detailsRoot}>
        <PostDetail
          expandedReplies={expandedReplies}
          post={selectedPost}
          reddit={reddit}
        />
      </Portal>

      <Portal mountNode={nodes.detailsBarRoot}>
        <DetailsBar title={selectedPost.title} />
      </Portal>

      {galleryImages && galleryImages.length > 1 && (
        <Portal mountNode={nodes.modalRoot}>
          <Modal
            onCloseRequest={() => {
              openImageGallery([]);
            }}
            forGallery
          >
            <ImageGallery
              images={galleryImages}
              onCloseRequest={() => {
                openImageGallery([]);
              }}
            />
          </Modal>
        </Portal>
      )}

      {showingImageModal && (
        <Portal mountNode={nodes.modalRoot}>
          <ImageModal imageURL={modalImageUrl} />
        </Portal>
      )}

      {showingSubredditAdder && (
        <Portal mountNode={nodes.modalRoot}>
          <Modal onCloseRequest={closeModal}>
            <SubredditAdder
              title="Go to Subreddit:"
              reddit={reddit}
              onComplete={subreddit => {
                openSubreddit(subreddit);
                closeModal();
              }}
            />
          </Modal>
        </Portal>
      )}

      {showingChannelCreator && (
        <Portal mountNode={nodes.modalRoot}>
          <Modal onCloseRequest={closeModal}>
            <ChannelCreator reddit={reddit} onComplete={closeModal} />
          </Modal>
        </Portal>
      )}

      {message && (
        <Portal mountNode={nodes.messageRoot}>
          <Message
            text={message.text}
            type={message.type}
            closeTimeout={message.duration}
            onClosed={() => {
              dispatch(setMessage(null));
            }}
          />
        </Portal>
      )}
    </>
  );
}

function renderApp(reddit) {
  store.dispatch(
    setSelection(Store.get(Store.keys.SELECTION) || DefaultData.selection)
  );
  store.dispatch(setUsername(Store.get(Store.keys.USERNAME) || ''));
  store.dispatch(
    setExpandedReplies(Store.get(Store.keys.EXPANDED_REPLIES) || false)
  );
  store.dispatch(
    setBigThumbnails(Store.get(Store.keys.BIG_THUMBNAILS) || false)
  );
  store.dispatch(setPreferences(getPreferences()));
  store.dispatch(setSubreddits(Store.get(Store.keys.SUBREDDITS) || []));
  store.dispatch(setChannels(Store.get(Store.keys.CHANNELS) || []));
  const root = createRoot(nodes.appRoot);
  root.render(
    <Provider store={store}>
      <App reddit={reddit} />
    </Provider>
  );
}

function renderStartScreen() {
  setAccentColors();
  const root = createRoot(document.body);
  root.render(
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        marginLeft: '0.5rem',
        marginRight: '0.5rem',
        flexDirection: 'column',
      }}
    >
      <h1 style={{ marginBottom: 0 }}>🛰 Satelite (βeta)</h1>
      <i style={{ textAlign: 'center', marginTop: 0 }}>
        A swift & lean Reddit web client
      </i>
      <p style={{ textAlign: 'center' }}>
        Enjoy a faster, ad-free, and popup-free reddit experience
      </p>
      <a
        href="/auth/"
        className="MainButton MainButton--welcome"
        style={{
          color: 'white',
          fontWeight: '700',
          backgroundImage:
            'linear-gradient(90deg, #5D24CE 0%, #C1226F 52.08%, #C58823 100%)',
        }}
      >
        Launch Satelite
      </a>
      <i
        className="UserContent"
        style={{ textAlign: 'center', marginBlockStart: '3rem' }}
      >
        built by <a href="https://berbaquero.com">bernardo baquero stand</a> in
        amsterdam
      </i>
    </div>
  );
}

function initializeRedditClient(refreshToken) {
  return new Snoowrap({
    userAgent: 'satelite',
    clientId: clientID,
    clientSecret: '',
    refreshToken: refreshToken,
  });
}

// INITIALIZE
// Check for reddit API token
const refreshToken = Store.get('rt');
if (refreshToken) {
  // Render app with new reddit client instance
  const reddit = initializeRedditClient(refreshToken);
  reddit.config({ proxies: false });
  renderApp(reddit);
} else {
  // if there's no token, show starting screen
  renderStartScreen();
}

// const withKeyClass = 'with-key';

// function focusOnColumn(columnIndex, withKey = false) {
//   switch (columnIndex) {
//     case 1:
//       Actions.toggleMenu(true);
//       if (withKey) {
//         nodes.menuRoot.classList.add(withKeyClass);
//       }
//       nodes.menuRoot.focus();
//       break;
//     case 2:
//       Actions.toggleMenu(false);
//       if (withKey) {
//         nodes.postsRoot.classList.add(withKeyClass);
//       }
//       nodes.postsRoot.focus();
//       break;
//     case 3:
//       Actions.toggleMenu(false);
//       if (withKey) {
//         nodes.detailsRoot.classList.add(withKeyClass);
//       }
//       nodes.detailsRoot.focus();
//       break;
//   }
// }

// Keyboard Events

// function handleCustomKeys(ev) {
//   const { keyCode, key } = ev;
//   switch (keyCode) {
//     case 27: // Esc
//       closeModal();
//       return;
//   }
//   if (!['1', '2', '3', 'j', 'k', 'h', 'l'].includes(key)) return;
//   switch (key) {
//     case '1':
//       focusOnColumn(1, true);
//       break;
//     case '2':
//       focusOnColumn(2, true);
//       break;
//     case '3':
//       focusOnColumn(3, true);
//       break;
//     case 'j':
//       smoothScrollY(getFocusedView(), 48, 16, ScrollDirection.down);
//       break;
//     case 'k':
//       smoothScrollY(getFocusedView(), 48, 16, ScrollDirection.up);
//       break;
//     case 'h':
//       focusColumn(-1);
//       break;
//     case 'l':
//       focusColumn(1);
//       break;
//   }
// }

function handleShortcutsInit(ev) {
  if (ev.key !== 'f') return;
  // import ZapKeys
  document.body.style.opacity = '.7'; // Add loading "indicator"
  Promise.all([
    import('zapkeys'),
    new Promise(function(resolve, reject) {
      const zapkeysCSS = document.createElement('link');
      zapkeysCSS.rel = 'stylesheet';
      zapkeysCSS.href = 'https://unpkg.com/zapkeys@0.2.0/dist/index.css';
      zapkeysCSS.onload = resolve();
      zapkeysCSS.onerror = reject();
      document.head.appendChild(zapkeysCSS);
    }),
  ])
    .then(([{ ZapKeys }]) => {
      document.body.style.opacity = ''; // Remove loading "indicator"
      const zapkeys = new ZapKeys();
      //   {
      //   onActive: () => {
      //     window.removeEventListener('keypress', handleCustomKeys);
      //   },
      //   onInactive: () => {
      //     window.addEventListener('keypress', handleCustomKeys);
      //   },
      // }
      zapkeys.init({ active: true });
      window.removeEventListener('keypress', handleShortcutsInit);
    })
    .catch(() => (document.body.style.opacity = '')); // Remove loading "indicator"
}

// Initialize shortcuts
// window.addEventListener('keypress', handleCustomKeys);

window.addEventListener('keypress', handleShortcutsInit);

// function getFocusedView() {
//   const { activeElement } = document;
//   if (nodes.menuRoot.contains(activeElement)) return nodes.menuRoot;
//   if (nodes.detailsRoot.contains(activeElement)) return nodes.detailsRoot;
//   return nodes.postsRoot;
// }

// function getColumnIndex(focusedView) {
//   if (focusedView === nodes.menuRoot) return 1;
//   if (focusedView === nodes.postsRoot) return 2;
//   if (focusedView === nodes.detailsRoot) return 3;
// }

// function focusColumn(direction) {
//   const focusedView = getFocusedView();
//   const currentIndex = getColumnIndex(focusedView);
//   const newIndex = currentIndex + direction;
//   if (newIndex < 1 || newIndex > 3) return;
//   focusOnColumn(newIndex, true);
// }
